import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { revealField } from '@/lib/passengers/api';
import { useCachedAccess } from '@/lib/access/use-access';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { formatPaxSummary, priorityShortLabel } from '@/routes/disruptions/labels';
import { CardAction, ServiceCard } from './pnr-card-ui';

// The card's «Pasajeros»: who travels, masked by default. «Mostrar nombres» reveals the
// whole party through the audited reveal (the BFF logs who/when, the value is never
// logged) and only for an operator holding `pii.name.reveal` — when access is not loaded
// yet the BFF decides. Their conditions, contact and acceptance live in the Passengers view.

const TYPE_KEY: Record<string, string> = { ADT: 'adult', CHD: 'child', INF: 'infant' };

export function PnrPaxSummary({ group, onOpen }: { group: DisruptionPnrGroupRow; onOpen: () => void }) {
  const { t } = useTranslation('pnr');
  const access = useCachedAccess();
  const mayReveal = !access.ready || access.can('pii.name.reveal');
  const [names, setNames] = useState<Record<string, string> | null>(null);
  const reveal = useMutation({
    mutationFn: async () => {
      const out: Record<string, string> = {};
      for (const p of group.passengers) {
        const r = await revealField(group.caseUrn, p.paxUrn, 'name');
        out[p.paxUrn] = r.value;
      }
      return out;
    },
    onSuccess: setNames,
  });
  const needs = group.needs ?? [];

  return (
    <ServiceCard
      title={t('card.pax.title', { count: group.paxCount })}
      status={formatPaxSummary(group.paxCount, group.paxBreakdown)}
      testId="pnr-card-passengers"
      actions={
        <>
          {mayReveal && group.passengers.length > 0 ? (
            names ? (
              <CardAction onClick={() => setNames(null)} testId="pnr-hide-names">
                {t('card.pax.hideNames')}
              </CardAction>
            ) : (
              <CardAction onClick={() => reveal.mutate()} disabled={reveal.isPending} testId="pnr-reveal-names">
                {t('card.pax.showNames', { count: group.passengers.length })}
              </CardAction>
            )
          ) : null}
          <CardAction onClick={onOpen} testId="pnr-open-passengers">
            {t('card.pax.open')}
          </CardAction>
        </>
      }
    >
      <ul className="flex flex-col gap-1.5" data-testid="pnr-card-pax-list">
        {group.passengers.map((p, idx) => (
          <li key={p.paxUrn} className="flex items-baseline justify-between gap-3 text-[15px]">
            <span className="min-w-0 truncate" data-testid="pnr-card-pax-name">
              {names?.[p.paxUrn] ?? p.fullName}
            </span>
            <span className="shrink-0 text-[13px] text-muted-foreground">
              {[t(`card.pax.type.${TYPE_KEY[p.paxType ?? 'ADT'] ?? 'adult'}`), idx === 0 ? t('card.pax.lead') : undefined]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </li>
        ))}
        {group.passengers.length === 0 ? (
          <li className="text-[15px] text-muted-foreground">{group.leadName}</li>
        ) : null}
      </ul>
      {reveal.isError ? (
        <p className="text-[13px] text-destructive" role="alert">
          {t('card.pax.revealFailed')}
        </p>
      ) : null}
      {group.priorityTags.length > 0 || needs.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {group.priorityTags.map((tag) => (
            <Badge key={tag} variant="warning" size="sm">
              {priorityShortLabel(tag, t)}
            </Badge>
          ))}
          {needs.length > 0 ? (
            <Badge variant="warning" size="sm" title={t('hero.needsTitle', { list: needs.join(', ') })}>
              {t('hero.needsCount', { count: needs.length })}
            </Badge>
          ) : null}
        </div>
      ) : null}
    </ServiceCard>
  );
}
